import { Box, Container, Flex } from "theme-ui";
import React, { ReactNode, useState } from "react";
//import { keyframes } from '@emotion/react'
import { Logo } from "../../components/Logo";
import { Link, useStaticQuery, graphql } from "gatsby";
import { Icon } from "../../optimizely-components/Utilities/Icons/Icon";
import SubNav from "./SubNav";
import LoginButton from "../../optimizely-components/Utilities/Buttons/LoginButton";
import { useSecureLinks } from "../../hooks/useSecureLinks";
import ApplyButton from "../../optimizely-components/Utilities/Buttons/ApplyButton";
import { StaticImage } from "gatsby-plugin-image";

/*
const growY = keyframes`
from {
  transform: scaleY(0);
}
to {
  transform: scaleY(1);
}
`
*/

interface LoginRowItemProps {
  /** proportional width */
  fr: number;
  children: ReactNode;
}
const LoginRowItem: React.FC<LoginRowItemProps> = (props) => {
  return (
    <div
      sx={{
        flexBasis: `${props.fr * 100}%`,
        flexGrow: 1,
        flexShrink: 0,
        borderRightWidth: 1,
        borderRightColor: "#333333",
        borderRightStyle: "solid",
        borderLeftWidth: 1,
        borderLeftColor: "#767676",
        borderLeftStyle: "solid",
        "&:last-of-type": {
          borderRight: "none",
        },
        "&:first-of-type": {
          borderLeft: "none",
        },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: 2,
        px: 0,
      }}
    >
      {props.children}
    </div>
  );
};

/**
 * Provides useful navigation links.
 */
export const Header = React.memo(() => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const secureLinks = useSecureLinks();
  const data = useStaticQuery(
    graphql`
      query {
        loginIcon: file(
          relativePath: { eq: "layouts/legacy/header-icon-login.png" }
        ) {
          childImageSharp {
            fixed(width: 24, height: 24) {
              ...GatsbyImageSharpFixed_withWebp_noBase64
            }
          }
        }
      }
    `
  );
  return (
    <Box
      as="header"
      sx={{
        flexDirection: "column",
        fontFamily: "body",
        boxShadow: "rgb(135, 135, 135) 0px 1px 2px 0px",
        zIndex: 2000,
        display: "flex",
        height: ["75px", null, null, "70px"],
        position: "sticky",
        borderBottom: "5px solid #645B88",
        bg: "#fff",
        borderBottomWidth: "5px",
        borderImage:
          "linear-gradient(to right, #645B88, #2E368F, #42368E, #D91828, #FFDD00) 1 stretch",
        alignItems: "baseline",
        top: 0,
      }}
      id="Site-Nav-Header"
    >
      <Flex
        as="nav"
        sx={{
          boxSizing: "border-box",
          minWidth: 0,
          width: "100%",
          bg: "#fff",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
        aria-label="Main Menu"
      >
        <Container
          sx={{
            pt: [0, null, null, "10px"],
            pb: 0,
            px: "15px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: [null, null, "100%", "1360px"],
          }}
        >
          <Box>
            <Link
              to="/"
              sx={{
                lineHeight: 0,
              }}
            >
              <Logo
                sx={{
                  width: [180, null, null, 220],
                  height: 35,
                  display: "block",
                  "&:focus": {
                    outline: "1px solid #1A1818",
                    border: "1px solid #1A1818",
                  },
                }}
              />
            </Link>
          </Box>
          <Box>
            <Box sx={{ width: "inherit" }}>
              <ul
                aria-labelledby="main-menu-heading"
                aria-label="Main Menu"
                role="list"
                sx={{
                  //alignItems: 'center',
                  listStyle: "none",
                  minHeight: "100%",
                  paddingLeft: 0,
                  flexDirection: ["row", "unset"],
                  alignItems: ["center"],
                  display: "flex",
                }}
              >
                <li
                  role="listitem"
                  sx={{
                    mx: "15px",
                    display: ["none", null, "flex"],
                  }}
                >
                  <Link
                    to="/contact-us/"
                    sx={{
                      fontWeight: "700",
                      "&:hover": { color: "brand.accentBlue" },
                    }}
                  >
                    Contact Us
                  </Link>
                </li>
                <li
                  role="listitem"
                  sx={{
                    mx: "15px",
                    display: ["flex"],
                  }}
                >
                  <LoginButton href={secureLinks.login} />
                </li>
                <StaticImage
                  src="../../images/gradient-separator.jpg"
                  alt="separator"
                  placeholder="none"
                  sx={{
                    display: ["block", null, "none"],
                  }}
                />
                <li
                  role="listitem"
                  sx={{
                    mx: "15px",
                    display: ["flex", null, "none"],
                  }}
                >
                  <ApplyButton href={secureLinks.login} />
                </li>
                <li role="listitem" sx={{ display: ["none", null, "flex"] }}>
                  <a
                    href={secureLinks.loanApplication}
                    sx={{
                      variant: "buttons.newPrimary",
                      mx: 3,
                      width: "212px",
                    }}
                  >
                    Apply Now
                  </a>
                </li>
                <li role="listitem">
                  <button
                    onClick={() => setIsNavOpen((open) => !open)}
                    sx={{
                      appearance: "none",
                      bg: "transparent",
                      px: "10px",
                      py: 2,
                      display: ["block"],
                      border: "0",
                      borderRadius: "2px",
                      outline: "none",
                      "&:focus": {
                        border: "1px solid #1A1818",
                        outline: "1px solid #1A1818",
                      },
                      "&:hover": {
                        border: "none",
                      },
                    }}
                    title="Toggle main navigation"
                    aria-label="Menu Button"
                    aria-expanded={isNavOpen ? true : false}
                  >
                    <span
                      sx={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: "hidden",
                        clip: "rect(0, 0, 0, 0)",
                        border: 0,
                      }}
                    >
                      Toggle navigation
                    </span>
                    {isNavOpen ? (
                      <Icon
                        color="black"
                        name={"close"}
                        size={22}
                        alt={"Close Menu"}
                      />
                    ) : (
                      <Icon
                        color="black"
                        name={"hambuger-menu"}
                        alt={"Haburger Menu"}
                        size={25}
                      />
                    )}
                  </button>
                </li>
              </ul>
            </Box>
          </Box>
        </Container>
        <div
          sx={{
            display: ["block", "none"],
            width: "100%",
          }}
        >
          <div
            sx={{
              display: [isNavOpen ? "block" : "none", "none"],
              transformOrigin: `center top`,
              //animation: [`${growY} 120ms ease-out 1`, null, null, 'none'],
              width: "100%",
              justifyContent: "space-around",
              paddingTop: "15px",
              position: [isNavOpen ? "absolute" : "relative"],
              top: "70px",
              bg: "#232C3A",
              height: ["100vh", "100%"],
              maxWidth: "100%",
            }}
          >
            <SubNav />
          </div>
        </div>
        <div
          sx={{
            display: ["none", "flex"],
            width: "100%",
          }}
        >
          <div
            sx={{
              display: [isNavOpen ? "flex" : "none"],
              transformOrigin: `center top`,
              //animation: [`${growY} 120ms ease-out 1`, null, null, 'none'],
              width: "100%",
              justifyContent: "space-around",
              paddingTop: "15px",
              position: [isNavOpen ? "absolute" : "relative"],
              top: "70px",
              zIndex: "-100000",
              bg: "#232C3A",
              maxWidth: "100%",
              height: ["100vh", null, null, "auto"],
              overflow: ["scroll", "auto"],
              transition: `max-height 0.35s ease-out`,
            }}
          >
            <SubNav />
          </div>
        </div>
      </Flex>
    </Box>
  );
});
